<template>
  <div class="text-center">
    <v-dialog
      v-model="$store.usersStore.state.editUserProjectsModal"
      max-width="760"
      scrollable
      persistent
    >
      <v-card
        :loading="$store.usersStore.state.loadingJobTypes || loading_user_projects"
        :disabled="$store.usersStore.state.loadingJobTypes || loading_user_projects"
        class="up-card"
        elevation="0"
      >
        <!-- Header -->
        <div class="up-header">
          <div class="d-flex align-center" style="gap: 12px;">
            <div class="up-avatar">
              <v-icon color="white" size="20">mdi-account-plus-outline</v-icon>
            </div>
            <div>
              <p class="up-title mb-0">
                Proyectos de: <strong>{{ $store.usersStore.state.selectedUserInfo.name }}</strong>
              </p>
              <p class="up-subtitle mb-0">Gestión de proyectos asignados</p>
            </div>
          </div>
          <v-btn icon small @click="closeModal" class="up-close-btn">
            <v-icon size="18" color="white">mdi-close</v-icon>
          </v-btn>
        </div>

        <v-card-text class="pa-0">
          <div class="up-body">

            <!-- PASO 1: Proyectos asignados -->
            <div class="up-step">
              <div class="up-step-header">
                <div class="up-step-badge">1</div>
                <div>
                  <p class="up-step-title mb-0">Proyectos asignados</p>
                  <p class="up-step-hint mb-0">Agrega o elimina los proyectos a los que tiene acceso el usuario.</p>
                </div>
              </div>

              <!-- Selector para agregar -->
              <div class="d-flex align-center mt-3" style="gap: 10px;">
                <v-autocomplete
                  v-model="project_to_add"
                  :items="availableProjects"
                  :loading="!$store.projectsStore.state.projectsList"
                  item-text="name"
                  item-value="id"
                  label="Buscar y agregar proyecto..."
                  outlined
                  dense
                  hide-details
                  clearable
                  return-object
                  class="up-select"
                  prepend-inner-icon="mdi-magnify"
                />
                <v-btn
                  depressed
                  color="#2D4B95"
                  dark
                  :disabled="!project_to_add"
                  class="up-add-btn"
                  @click="addProject"
                >
                  <v-icon size="16" class="mr-1">mdi-plus</v-icon>
                  Agregar
                </v-btn>
              </div>

              <!-- Lista de proyectos asignados -->
              <div class="up-projects-list mt-3">
                <div v-if="loading_user_projects" class="up-empty-state">
                  <v-progress-circular indeterminate color="#2D4B95" size="24" />
                </div>
                <div v-else-if="user_projects.length === 0" class="up-empty-state">
                  <v-icon size="32" color="#C5D0EA">mdi-folder-off-outline</v-icon>
                  <p class="up-empty-text mb-0">Sin proyectos asignados</p>
                </div>
                <div v-else>
                  <div
                    v-for="(item, index) in user_projects"
                    :key="item.id"
                    class="up-project-row"
                    :class="{ 'up-project-row--selected': selected_project_id === item.id }"
                    @click="selectProject(item)"
                  >
                    <div class="d-flex align-center" style="gap: 8px; flex: 1; min-width: 0;">
                      <div class="up-project-icon">
                        <v-icon size="14" color="#2D4B95">mdi-briefcase-outline</v-icon>
                      </div>
                      <p class="up-project-name mb-0">{{ item.name }}</p>
                      <v-chip
                        v-if="item.job_label"
                        x-small
                        outlined
                        color="#2D4B95"
                        class="up-job-chip"
                      >{{ item.job_label }}</v-chip>
                    </div>
                    <div class="d-flex align-center" style="gap: 6px;">
                      <v-tooltip bottom>
                        <template #activator="{ on }">
                          <v-btn icon x-small v-on="on" @click.stop="selectProject(item)">
                            <v-icon size="16" :color="selected_project_id === item.id ? '#2D4B95' : '#aaa'">
                              mdi-pencil-outline
                            </v-icon>
                          </v-btn>
                        </template>
                        <span>Editar cargo en este proyecto</span>
                      </v-tooltip>
                      <v-tooltip bottom>
                        <template #activator="{ on }">
                          <v-btn icon x-small v-on="on" @click.stop="removeProject(index, item.id)">
                            <v-icon size="16" color="#F4516C">mdi-close-circle-outline</v-icon>
                          </v-btn>
                        </template>
                        <span>Quitar del proyecto</span>
                      </v-tooltip>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <v-divider class="up-divider" />

            <!-- PASO 2: Editar cargo en proyecto seleccionado -->
            <div class="up-step" ref="jobSection">
              <div class="up-step-header">
                <div class="up-step-badge" :class="{ 'up-step-badge--disabled': !selected_project_id }">2</div>
                <div>
                  <p class="up-step-title mb-0">
                    Cargo en el proyecto
                    <span v-if="selectedProjectObj" class="up-step-project-name">
                      — {{ selectedProjectObj.name }}
                    </span>
                  </p>
                  <p class="up-step-hint mb-0">
                    <span v-if="!selected_project_id">Selecciona un proyecto de la lista de arriba para editar su cargo.</span>
                    <span v-else>Asigna el cargo que tendrá el usuario dentro del proyecto seleccionado.</span>
                  </p>
                </div>
              </div>

              <div class="up-job-selector mt-3" :class="{ 'up-job-selector--disabled': !selected_project_id }">
                <v-select
                  v-model="selected_job"
                  :items="$store.usersStore.state.jobTypes"
                  :loading="$store.usersStore.state.loadingJobTypes"
                  :disabled="!selected_project_id"
                  item-text="name"
                  item-value="id"
                  label="Seleccione cargo"
                  outlined
                  dense
                  hide-details
                  class="up-select"
                  prepend-inner-icon="mdi-briefcase-account-outline"
                  :rules="selected_project_id ? requiredRule : []"
                />
                <v-btn
                  depressed
                  color="#2D4B95"
                  dark
                  :disabled="!selected_project_id || !selected_job"
                  :loading="loadingJobSave"
                  class="up-add-btn"
                  @click="saveProjectJob"
                >
                  <v-icon size="16" class="mr-1">mdi-content-save-outline</v-icon>
                  Guardar
                </v-btn>
              </div>
            </div>

          </div>
        </v-card-text>

        <!-- Footer -->
        <div class="up-footer">
          <v-btn rounded outlined depressed class="up-btn-cancel" @click="closeModal">
            Cerrar
          </v-btn>
        </div>

      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { setUserProjects, removeUserFromProject } from '@/helpers/api/user'
import { getProjectsByUserID, getProjectsByUser } from '@/helpers/api/project'

export default {
  name: 'EditUserProjectsModal',
  data: () => ({
    validform: true,
    user_projects: [],
    project_to_add: null,
    selected_project_id: null,
    selected_job: null,
    loading_user_projects: true,
    loadingJobSave: false,
    requiredRule: [
      v => !!v || 'Campo requerido'
    ],
  }),
  computed: {
    selectedProject () {
      return this.$store.projectsStore.state.selectedProject
    },
    userInfo () {
      return this.$store.userStore.state.userInfo
    },
    // Proyectos disponibles para agregar (excluye los ya asignados)
    availableProjects () {
      const assigned = new Set(this.user_projects.map(p => p.id))
      const all = this.$store.projectsStore.state.projectsList || []
      return all.filter(p => !assigned.has(p.id))
    },
    // Objeto completo del proyecto seleccionado
    selectedProjectObj () {
      if (!this.selected_project_id) return null
      return this.user_projects.find(p => p.id === this.selected_project_id) || null
    },
  },
  mounted () {
    const projectID = this.$store.projectsStore.state.selectedProject.id
    this.$store.usersStore.dispatch('jobTypesGet', projectID)
    this.getProjectList()
    this.getUserProjects()
  },
  methods: {
    // Cargar lista de proyectos disponibles si aún no está en el store
    async getProjectList () {
      if (!this.$store.projectsStore.state.projectsList) {
        const projects = await getProjectsByUser()
        if (projects.code === 200) {
          this.$store.projectsStore.commit('setProjectsList', projects.projects)
        }
      }
    },

    // Seleccionar proyecto para editar cargo
    selectProject (item) {
      this.selected_project_id = item.id
      this.selected_job = item.job || null
      this.$nextTick(() => {
        const jobEl = this.$refs.jobSection
        if (!jobEl) return
        // El contenedor scrolleable es el v-card-text, buscarlo hacia arriba
        let scrollable = jobEl.parentElement
        while (scrollable) {
          if (scrollable.scrollHeight > scrollable.clientHeight) break
          scrollable = scrollable.parentElement
        }
        if (scrollable) {
          scrollable.scrollTo({ top: scrollable.scrollHeight, behavior: 'smooth' })
        }
      })
    },

    // Agregar proyecto a la lista (sin cargo aún)
    addProject () {
      if (!this.project_to_add) return
      const defaultJob = this.$store.usersStore.state.selectedUserInfo?.info?.default_job || null
      this.user_projects.push({
        ...this.project_to_add,
        job: defaultJob,
        job_label: null,
      })
      this.project_to_add = null
    },

    // Guardar el cargo en el proyecto seleccionado
    async saveProjectJob () {
      if (!this.selected_project_id || !this.selected_job) return
      this.loadingJobSave = true

      // Actualizar etiqueta del cargo en la lista local
      const idx = this.user_projects.findIndex(p => p.id === this.selected_project_id)
      if (idx !== -1) {
        const jobType = this.$store.usersStore.state.jobTypes.find(j => j.id === this.selected_job)
        this.user_projects[idx].job = this.selected_job
        this.user_projects[idx].job_label = jobType ? jobType.name : null
        this.user_projects = [...this.user_projects] // trigger reactivity
      }

      // Persistir en backend
      const data = {
        user_id: this.$store.usersStore.state.selectedUserInfo.id,
        user_projects: [{ id: this.selected_project_id, job: this.selected_job }],
      }
      const result = await setUserProjects(data)
      if (result.code === 200) {
        this.$store.globalStore.dispatch('addMessage', {
          message: 'Cargo actualizado correctamente en el proyecto.',
          title: 'Cargo actualizado',
          created: new Date(),
          type: 'success',
          icon: 'mdi-check-circle',
          color: '#3EBFA3',
        })
        if (this.selected_project_id === this.selectedProject.id) {
          this.getUsersList()
        }
      } else {
        this.$store.globalStore.dispatch('addMessage', {
          message: 'Hubo un error al guardar. Intenta nuevamente.',
          title: 'Error',
          created: new Date(),
          type: 'error',
          icon: 'mdi-alert-circle',
          color: '#fb8c00',
        })
      }
      this.loadingJobSave = false
    },

    // Eliminar proyecto
    async removeProject (index, project_id) {
      let projects = [...this.user_projects]
      projects.splice(index, 1)
      this.user_projects = projects

      if (this.selected_project_id === project_id) {
        this.selected_project_id = null
        this.selected_job = null
      }

      const data = {
        project_id,
        user_id: this.$store.usersStore.state.selectedUserInfo.id,
      }
      const user = await removeUserFromProject(data)
      if (user.code === 200) {
        this.$store.globalStore.dispatch('addMessage', {
          message: 'El usuario ha sido eliminado del proyecto.',
          title: 'Usuario eliminado',
          created: new Date(),
          type: 'success',
          icon: 'mdi-check-circle',
          color: '#3EBFA3',
        })
        if (project_id === this.selectedProject.id) {
          this.getUsersList()
        }
      }
    },

    async getUsersList () {
      const data = {
        project_id: this.selectedProject.id,
        start: 0,
        limit: 20,
      }
      this.$store.usersStore.dispatch('getUsersList', data)
    },

    async getUserProjects () {
      this.loading_user_projects = true
      const projects = await getProjectsByUserID({
        user_id: this.$store.usersStore.state.selectedUserInfo.id,
      })
      if (projects.code === 200) {
        const jobTypes = this.$store.usersStore.state.jobTypes || []
        this.user_projects = projects.projects.map(p => {
          const jobId = p.userjobtype?.job_type_id || null
          const jobType = jobTypes.find(j => j.id === jobId)
          return { ...p, job: jobId, job_label: jobType?.name || null }
        })
      }
      this.loading_user_projects = false
    },

    closeModal () {
      this.$store.usersStore.commit('setSelectedUserInfo', null)
      this.$store.usersStore.commit('setEditUserProjectsModal', false)
      this.user_projects = []
      this.project_to_add = null
      this.selected_project_id = null
      this.selected_job = null
      this.validform = true
      this.loadingJobSave = false
    },
  },
}
</script>

<style scoped>
.up-card {
  border-radius: 12px !important;
  overflow: hidden;
}

/* ── Header ───────────────────────────────────────── */
.up-header {
  background-color: #1C3A7A;
  padding: 16px 20px;
  display: flex;
  align-items: center;
  justify-content: space-between;
}
.up-avatar {
  width: 38px;
  height: 38px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.15);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}
.up-title    { font-size: 14px; font-weight: 400; color: #ffffff; }
.up-subtitle { font-size: 11px; color: rgba(255, 255, 255, 0.6); }
.up-close-btn { background: rgba(255, 255, 255, 0.1) !important; }
.up-close-btn:hover { background: rgba(220, 60, 60, 0.45) !important; }

/* ── Body ─────────────────────────────────────────── */
.up-body { padding: 20px 24px 8px; }

/* ── Steps ────────────────────────────────────────── */
.up-step { margin-bottom: 4px; }
.up-step-header { display: flex; align-items: flex-start; gap: 12px; }

.up-step-badge {
  min-width: 24px;
  height: 24px;
  border-radius: 50%;
  background: #2D4B95;
  color: #fff;
  font-size: 12px;
  font-weight: 600;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  margin-top: 2px;
}
.up-step-badge--disabled { background: #C5D0EA; }

.up-step-title {
  font-size: 13px;
  font-weight: 600;
  color: #2D4B95;
}
.up-step-project-name {
  font-weight: 400;
  color: #555;
}
.up-step-hint {
  font-size: 12px;
  color: #707070;
  line-height: 1.5;
  margin-top: 2px;
}

.up-divider { margin: 20px 0; }

/* ── Selector + botón ─────────────────────────────── */
.up-select { flex: 1; }
.up-add-btn {
  height: 40px !important;
  white-space: nowrap;
  font-size: 13px !important;
  text-transform: none !important;
  letter-spacing: 0 !important;
  border-radius: 8px !important;
}

/* ── Lista de proyectos ───────────────────────────── */
.up-projects-list {
  border: 1px solid #E0E6F5;
  border-radius: 10px;
  overflow: hidden;
  min-height: 60px;
}

.up-empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 24px;
  min-height: 80px;
}
.up-empty-text { font-size: 12px; color: #aaa; }

.up-project-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 14px;
  border-bottom: 1px solid #F0F4FB;
  cursor: pointer;
  transition: background 0.15s;
  gap: 8px;
}
.up-project-row:last-child { border-bottom: none; }
.up-project-row:hover { background: #F5F7FD; }
.up-project-row--selected { background: #EEF2FB; }

.up-project-icon {
  width: 26px;
  height: 26px;
  border-radius: 6px;
  background: #EEF2FB;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}
.up-project-name {
  font-size: 13px;
  font-weight: 500;
  color: #333;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.up-job-chip { font-size: 10px !important; }

/* ── Job selector (paso 2) ────────────────────────── */
.up-job-selector {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 14px;
  border: 1px solid #E0E6F5;
  border-radius: 10px;
  background: #FAFBFF;
  transition: opacity 0.2s;
}
.up-job-selector--disabled {
  opacity: 0.45;
  pointer-events: none;
}

/* ── Footer ───────────────────────────────────────── */
.up-footer {
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 8px;
  padding: 14px 24px;
  border-top: 1px solid #F0F0F0;
}
.up-btn-cancel { color: #2D4B95 !important; border-color: #C5D0EA !important; }

/* ── Responsive ───────────────────────────────────── */
@media (max-width: 540px) {
  .up-job-selector { flex-direction: column; align-items: stretch; }
  .up-add-btn { width: 100%; }
}
</style>